import {FC, useCallback, useEffect, useRef, useState} from 'react'
import Swal from 'sweetalert2'
import {api, HostApplication} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.aiiplan.com/api').replace('/api', '')
const docSrc = (url?: string | null) => (url ? (url.startsWith('http') ? url : `${API_BASE}${url}`) : null)

type ProviderStatus = 'approved' | 'pending' | 'rejected'

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}

function statusStyle(s: ProviderStatus) {
  if (s === 'approved') return {bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0'}
  if (s === 'rejected') return {bg: '#fff1f2', color: '#e11d48', border: '#fecdd3'}
  return {bg: '#fefce8', color: '#ca8a04', border: '#fde68a'}
}

function statusLabel(s: ProviderStatus) {
  if (s === 'rejected') return 'suspended'
  return s
}

// ── Action Menu ───────────────────────────────────────────────────────────────
const ProviderActionMenu: FC<{app: HostApplication; onView: () => void; onApprove: () => void; onSuspend: () => void; onDelete: () => void}> = ({app, onView, onApprove, onSuspend, onDelete}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  return (
    <div ref={ref} style={{position: 'relative', display: 'inline-block'}}>
      <button onClick={() => setOpen(o => !o)}
        style={{background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, color: '#94a3b8', fontSize: 20, lineHeight: 1}}>⋮</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 200, minWidth: 170, overflow: 'hidden', marginTop: 4,
        }}>
          <button onClick={() => { onView(); setOpen(false) }}
            style={{display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', color: '#374151'}}
            onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/><circle cx='12' cy='12' r='3'/></svg>
            View Details
          </button>
          {app.status === 'pending' && (
            <button onClick={() => { onApprove(); setOpen(false) }}
              style={{display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', color: '#16a34a'}}
              onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='20 6 9 17 4 12'/></svg>
              Approve
            </button>
          )}
          <div style={{height: 1, background: '#f1f5f9'}} />
          <button onClick={() => { onSuspend(); setOpen(false) }}
            style={{display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', color: '#ef4444'}}
            onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><circle cx='12' cy='12' r='10'/><line x1='4.93' y1='4.93' x2='19.07' y2='19.07'/></svg>
            Suspend
          </button>
          <div style={{height: 1, background: '#f1f5f9'}} />
          <button onClick={() => { onDelete(); setOpen(false) }}
            style={{display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', color: '#dc2626'}}
            onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'/></svg>
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────
const ProviderDetailDrawer: FC<{
  app: HostApplication
  categoryLabel: string
  onClose: () => void
  onApprove: (id: string) => Promise<void>
  onReject: (id: string, reason: string) => Promise<void>
  onVerify: (id: string, flags: Partial<HostApplication>) => Promise<void>
  onSuspend: (id: string, reason: string) => Promise<void>
  onReactivate: (id: string) => Promise<void>
  onFinalApprove: (id: string) => Promise<void>
}> = ({app, categoryLabel, onClose, onApprove, onReject, onVerify, onSuspend, onReactivate, onFinalApprove}) => {
  const [loading, setLoading]     = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason]       = useState('')
  const [error, setError]         = useState('')
  const [verifyingKey, setVerifyingKey] = useState<string | null>(null)
  const ss = statusStyle(app.status)

  const handleSuspend = async () => {
    setLoading(true); setError('')
    try { await onSuspend(app.id, reason); onClose() } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }
  const handleReactivate = async () => {
    setLoading(true); setError('')
    try { await onReactivate(app.id); onClose() } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }
  const handleFinalApprove = async () => {
    setLoading(true); setError('')
    try { await onFinalApprove(app.id); onClose() } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  // Every applicable document has been verified — ready for the final team sign-off.
  const allVerified =
    (app.licenseApplicable === false || !!app.verifiedBusinessLicense) &&
    (app.insuranceApplicable === false || !!app.verifiedInsurance) &&
    (app.certificationApplicable === false || !!app.verifiedCertification) &&
    !!app.verifiedGovId && !!app.verifiedBackgroundCheck

  const VerifyToggle: FC<{label: string; field: keyof HostApplication}> = ({label, field}) => {
    const on = !!app[field]
    return (
      <button
        disabled={verifyingKey === field}
        onClick={async () => { setVerifyingKey(field); try { await onVerify(app.id, {[field]: !on}) } finally { setVerifyingKey(null) } }}
        style={{
          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
          border: `1px solid ${on ? '#bbf7d0' : '#e2e8f0'}`,
          background: on ? '#f0fdf4' : '#fff', color: on ? '#16a34a' : '#64748b',
        }}>
        {verifyingKey === field ? '…' : on ? '✓ Verified' : `Verify ${label}`}
      </button>
    )
  }

  const handleApprove = async () => {
    setLoading(true); setError('')
    try { await onApprove(app.id) } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const handleReject = async () => {
    setLoading(true); setError('')
    try { await onReject(app.id, reason); onClose() } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const Field: FC<{label: string; value?: string | number}> = ({label, value}) => (
    <div style={{display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f8fafc'}}>
      <span style={{fontSize: 13, color: '#64748b'}}>{label}</span>
      <span style={{fontSize: 13, fontWeight: 500, color: '#0f172a', textAlign: 'right', maxWidth: '60%'}}>{value || '—'}</span>
    </div>
  )

  return (
    <>
      <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 200}} />
      <div style={{position: 'fixed', top: 0, right: 0, height: '100vh', width: 480, background: '#fff', zIndex: 201, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column'}}>
        <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0}}>
          <h3 style={{fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0}}>Provider Details</h3>
          <button onClick={onClose} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
          </button>
        </div>
        <div style={{flex: 1, overflowY: 'auto', padding: '24px'}}>
          {error && <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16}}>{error}</div>}

          <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 12}}>
            <div style={{width: 64, height: 64, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 22, flexShrink: 0}}>
              {getInitials(app.fullName)}
            </div>
            <div>
              <div style={{fontSize: 18, fontWeight: 700, color: '#0f172a'}}>{app.fullName}</div>
              <div style={{fontSize: 13, color: '#64748b', marginTop: 2}}>{app.email}</div>
              <span style={{display: 'inline-block', marginTop: 6, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                {statusLabel(app.status)}
              </span>
            </div>
          </div>

          <div style={{marginBottom: 24}}>
            <div style={{fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10}}>Business Info</div>
            <Field label='Business Name' value={app.businessName} />
            <Field label='Category' value={categoryLabel || app.businessType} />
            <Field label='State' value={app.stateRegion} />
            <Field label='City' value={app.city} />
            <Field label='Experience' value={app.yearsExperience ? `${app.yearsExperience} years` : undefined} />
            {app.packages && app.packages.length > 0 ? (
              <div style={{marginTop: 10}}>
                <div style={{fontSize: 13, color: '#64748b', marginBottom: 6}}>Packages</div>
                {app.packages.map((pkg, i) => (
                  <div key={i} style={{fontSize: 13, color: '#0f172a', background: '#f8fafc', padding: '8px 12px', borderRadius: 8, marginBottom: 6}}>
                    <strong>{pkg.name || `Package ${i + 1}`}</strong> — {pkg.pricingModel}{pkg.baseRate ? ` · $${pkg.baseRate}` : ''}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <Field label='Pricing Model' value={app.pricingModel} />
                <Field label='Base Rate' value={app.baseRate ? `$${app.baseRate}` : undefined} />
              </>
            )}
          </div>

          <div style={{marginBottom: 24}}>
            <div style={{fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10}}>Contact</div>
            <Field label='Phone' value={app.phone} />
            <Field label='Address' value={app.address} />
          </div>

          {app.serviceDescription && (
            <div style={{marginBottom: 24}}>
              <div style={{fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8}}>Description</div>
              <div style={{fontSize: 13, color: '#374151', background: '#f8fafc', padding: '10px 12px', borderRadius: 8, lineHeight: 1.6}}>{app.serviceDescription}</div>
            </div>
          )}

          <div style={{marginBottom: 24}}>
            <div style={{fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10}}>Documents & Verification</div>
            {([
              {label: 'Business License',           applicable: app.licenseApplicable,       url: app.businessLicenseUrl,    field: 'verifiedBusinessLicense' as const},
              {label: 'Liability Insurance',        applicable: app.insuranceApplicable,     url: app.liabilityInsuranceUrl, field: 'verifiedInsurance' as const},
              {label: 'Professional Certification', applicable: app.certificationApplicable, url: app.professionalCertUrl,    field: 'verifiedCertification' as const},
            ]).map(d => {
              const src = docSrc(d.url)
              return (
                <div key={d.label} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f8fafc'}}>
                  <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                    <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>{d.label}</span>
                    {src ? (
                      <a href={src} target='_blank' rel='noreferrer' style={{fontSize: 12, fontWeight: 600, color: '#7c3aed'}}>View document</a>
                    ) : d.applicable === false ? (
                      <span style={{fontSize: 12, color: '#94a3b8'}}>Not applicable</span>
                    ) : (
                      <span style={{fontSize: 12, color: '#f59e0b', fontWeight: 600}}>Pending upload</span>
                    )}
                  </div>
                  {/* Only allow verifying once the document is actually uploaded. */}
                  {src && <VerifyToggle label={d.label.split(' ')[0]} field={d.field} />}
                </div>
              )
            })}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f8fafc'}}>
              <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>Government-issued ID</span>
                <div style={{display: 'flex', gap: 12}}>
                  {([{label: 'Front', url: app.govIdFrontUrl}, {label: 'Back', url: app.govIdBackUrl}]).map(s => {
                    const src = docSrc(s.url)
                    return src
                      ? <a key={s.label} href={src} target='_blank' rel='noreferrer' style={{fontSize: 12, fontWeight: 600, color: '#7c3aed'}}>{s.label}</a>
                      : <span key={s.label} style={{fontSize: 12, color: '#ef4444'}}>{s.label}: missing</span>
                  })}
                </div>
              </div>
              {app.govIdFrontUrl && app.govIdBackUrl && <VerifyToggle label='ID' field='verifiedGovId' />}
            </div>
            <Field label='Legal Name' value={app.legalName || undefined} />
            <Field label='Date of Birth' value={app.dateOfBirth || undefined} />
            <Field label='SSN' value={app.ssnLast4 ? `•••–••–${app.ssnLast4}` : undefined} />
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0'}}>
              <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>Background Check</span>
                <span style={{fontSize: 12, color: app.backgroundConsent ? '#16a34a' : '#94a3b8'}}>
                  Consent: {app.backgroundConsent ? 'Authorized' : 'Not authorized'}
                </span>
              </div>
              {app.backgroundConsent && <VerifyToggle label='Check' field='verifiedBackgroundCheck' />}
            </div>
          </div>

          {rejecting && (
            <div style={{marginTop: 8}}>
              <label style={{fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6}}>
                {app.status === 'approved' ? 'Suspension Reason' : 'Rejection Reason'} <span style={{color: '#ef4444'}}>*</span>
              </label>
              <textarea rows={3} placeholder='Explain why...' value={reason} onChange={e => setReason(e.target.value)}
                style={{width: '100%', padding: '10px 12px', border: `1px solid ${!reason.trim() ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'}} />
            </div>
          )}
        </div>
        <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0}}>
          {rejecting ? (
            <>
              <button onClick={() => { setRejecting(false); setReason('') }} disabled={loading}
                style={{padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>Cancel</button>
              <button onClick={app.status === 'approved' ? handleSuspend : handleReject} disabled={loading || !reason.trim()}
                style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#ef4444', fontSize: 14, cursor: 'pointer', color: '#fff', fontWeight: 600, opacity: !reason.trim() ? 0.5 : 1}}>
                {loading ? 'Saving...' : 'Confirm Suspend'}
              </button>
            </>
          ) : app.status === 'pending' ? (
            <>
              <button onClick={() => setRejecting(true)} disabled={loading}
                style={{padding: '9px 18px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', fontSize: 14, cursor: 'pointer', color: '#dc2626', fontWeight: 500}}>Suspend</button>
              <button onClick={handleApprove} disabled={loading}
                style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#7c3aed', fontSize: 14, cursor: 'pointer', color: '#fff', fontWeight: 600}}>
                {loading ? 'Approving...' : 'Approve'}
              </button>
            </>
          ) : app.status === 'approved' ? (
            <>
              {app.finalApproved ? (
                <span style={{padding: '9px 14px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 14, color: '#16a34a', fontWeight: 600}}>
                  ✓ Approved by Team
                </span>
              ) : allVerified ? (
                <button onClick={handleFinalApprove} disabled={loading}
                  style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#16a34a', fontSize: 14, cursor: 'pointer', color: '#fff', fontWeight: 600}}>
                  {loading ? 'Approving...' : 'Final Approval by Team'}
                </button>
              ) : null}
              <button onClick={() => setRejecting(true)} disabled={loading}
                style={{padding: '9px 18px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', fontSize: 14, cursor: 'pointer', color: '#dc2626', fontWeight: 600}}>
                Suspend Provider
              </button>
            </>
          ) : (
            <button onClick={handleReactivate} disabled={loading}
              style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#16a34a', fontSize: 14, cursor: 'pointer', color: '#fff', fontWeight: 600}}>
              {loading ? 'Reactivating...' : 'Reactivate Provider'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const ProvidersPage: FC = () => {
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({})
  const [stateFilter, setStateFilter] = useState('all')
  const [statusFilter, setStatus]     = useState('all')
  const [selected, setSelected]       = useState<HostApplication | null>(null)

  const {items: apps, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<HostApplication>(
      (p, l, s) => api.getHostApplicationsPaged(p, l, s, {
        state: stateFilter !== 'all' ? stateFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      }),
      {limit: 20, deps: [stateFilter, statusFilter]},
    )

  // True total of pending applications (the list is paginated, so the visible
  // page can't be counted). Fetched as total of a status=pending query.
  const [pendingCount, setPendingCount] = useState(0)
  const refreshPending = useCallback(() => {
    api.getHostApplicationsPaged(1, 1, '', {status: 'pending'})
      .then(r => setPendingCount(r.total))
      .catch(() => {})
  }, [])

  useEffect(() => {
    refreshPending()
    api.getServiceCategories()
      .then(cats => {
        const map: Record<string, string> = {}
        cats.forEach(c => { map[c.id] = c.label })
        setCategoryMap(map)
      })
      .catch(() => {})
  }, [refreshPending])

  const states = Array.from(new Set(apps.map(a => a.stateRegion).filter(Boolean))).sort()

  const handleApprove = async (id: string) => {
    try {
      await api.approveHostApplication(id)
      setSelected(prev => prev?.id === id ? {...prev, status: 'approved'} : prev)
      reload()
      refreshPending()
      toast('Provider approved')
    } catch (e: any) {
      toast(e?.message ?? 'Approve failed', 'error')
      throw e
    }
  }

  const handleDelete = async (app: HostApplication) => {
    const res = await Swal.fire({
      title: 'Delete provider?',
      html: `<b>${app.fullName}</b>'s application will be permanently removed. This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      confirmButtonColor: '#ef4444',
      cancelButtonText: 'Cancel',
    })
    if (!res.isConfirmed) return
    try {
      await api.deleteProvider(app.id)
      setSelected(prev => prev?.id === app.id ? null : prev)
      reload()
      refreshPending()
      toast(`${app.fullName} deleted`)
    } catch (e: any) {
      toast(e?.message ?? 'Delete failed', 'error')
    }
  }

  const handleReject = async (id: string, reason: string) => {
    try {
      await api.rejectHostApplication(id, reason)
      setSelected(prev => prev?.id === id ? {...prev, status: 'rejected'} : prev)
      reload()
      refreshPending()
      toast('Provider rejected')
    } catch (e: any) {
      toast(e?.message ?? 'Reject failed', 'error')
      throw e
    }
  }

  const handleFinalApprove = async (id: string) => {
    try {
      await api.finalApproveHostApplication(id)
      setSelected(prev => prev?.id === id ? {...prev, finalApproved: true} : prev)
      reload()
      refreshPending()
      toast('Provider given final approval')
    } catch (e: any) {
      toast(e?.message ?? 'Final approval failed', 'error')
      throw e
    }
  }

  const handleVerify = async (id: string, flags: Partial<HostApplication>) => {
    try {
      const updated = await api.updateHostVerification(id, flags)
      setSelected(prev => prev?.id === id ? {...prev, ...updated} : prev)
      reload()
      refreshPending()
      toast('Verification updated')
    } catch (e: any) {
      toast(e?.message ?? 'Verification update failed', 'error')
      throw e
    }
  }

  const handleSuspend = async (id: string, reason: string) => {
    try {
      await api.suspendProvider(id, reason)
      setSelected(prev => prev?.id === id ? {...prev, status: 'rejected', rejectionReason: reason} : prev)
      reload()
      refreshPending()
      toast('Provider suspended')
    } catch (e: any) {
      toast(e?.message ?? 'Suspend failed', 'error')
      throw e
    }
  }

  const handleReactivate = async (id: string) => {
    try {
      await api.reactivateProvider(id)
      setSelected(prev => prev?.id === id ? {...prev, status: 'approved', rejectionReason: undefined} : prev)
      reload()
      refreshPending()
      toast('Provider reactivated')
    } catch (e: any) {
      toast(e?.message ?? 'Reactivate failed', 'error')
      throw e
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24}}>
        <div>
          <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Service Providers</h1>
          <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage service provider applications and accounts</p>
        </div>
        <span style={{
          padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
          background: pendingCount > 0 ? '#fefce8' : '#f0fdf4',
          color: pendingCount > 0 ? '#a16207' : '#16a34a',
          border: `1px solid ${pendingCount > 0 ? '#fde68a' : '#bbf7d0'}`,
          whiteSpace: 'nowrap', marginTop: 4,
        }}>
          {pendingCount > 0
            ? `${pendingCount} Pending Application${pendingCount !== 1 ? 's' : ''}`
            : 'No Pending Applications'}
        </span>
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search providers...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', outline: 'none', boxSizing: 'border-box'}}
          />
        </div>

        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
          style={{flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All States</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select value={statusFilter} onChange={e => setStatus(e.target.value)}
          style={{flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All Status</option>
          <option value='approved'>Approved</option>
          <option value='pending'>Pending</option>
          <option value='rejected'>Suspended</option>
        </select>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{borderBottom: '1px solid #e2e8f0'}}>
                  {['PROVIDER', 'STATE', 'CATEGORY', 'EXPERIENCE', 'RATING', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apps.map((a, i) => {
                  const ss = statusStyle(a.status)
                  const catLabel = categoryMap[a.businessType] || a.businessType
                  return (
                    <tr key={a.id}
                      style={{borderBottom: i < apps.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                      onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                    >
                      {/* Provider */}
                      <td style={{padding: '16px 20px', minWidth: 240}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                          <div style={{
                            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                            background: '#7c3aed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 700, fontSize: 14,
                          }}>
                            {getInitials(a.fullName)}
                          </div>
                          <div>
                            <div style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{a.fullName}</div>
                            <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{a.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* State */}
                      <td style={{padding: '16px 20px', color: '#374151', whiteSpace: 'nowrap'}}>
                        {a.stateRegion || '—'}
                      </td>

                      {/* Category */}
                      <td style={{padding: '16px 20px', whiteSpace: 'nowrap'}}>
                        {catLabel ? (
                          <span style={{padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#eff6ff', color: '#3b82f6'}}>
                            {catLabel}
                          </span>
                        ) : <span style={{color: '#94a3b8'}}>—</span>}
                      </td>

                      {/* Experience */}
                      <td style={{padding: '16px 20px', color: '#374151', whiteSpace: 'nowrap'}}>
                        {a.yearsExperience ? `${a.yearsExperience} years` : '—'}
                      </td>

                      {/* Rating */}
                      <td style={{padding: '16px 20px', whiteSpace: 'nowrap'}}>
                        <span style={{color: '#94a3b8', fontSize: 13}}>N/A</span>
                      </td>

                      {/* Status */}
                      <td style={{padding: '16px 20px'}}>
                        <span style={{
                          padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                          background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`,
                        }}>
                          {statusLabel(a.status)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{padding: '16px 20px', textAlign: 'right'}}>
                        <ProviderActionMenu
                          app={a}
                          onView={() => setSelected(a)}
                          onApprove={async () => { if (window.confirm(`Approve ${a.fullName}?`)) await handleApprove(a.id) }}
                          onSuspend={() => setSelected(a)}
                          onDelete={() => handleDelete(a)}
                        />
                      </td>
                    </tr>
                  )
                })}
                {apps.length === 0 && (
                  <tr><td colSpan={7} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No providers found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {apps.length} of {total} providers</span>
            <div style={{display: 'flex', gap: 8}}>
              {[
                {label: 'Approved', count: apps.filter(a => a.status === 'approved').length, bg: '#f0fdf4', color: '#16a34a'},
                {label: 'Pending',  count: apps.filter(a => a.status === 'pending').length,  bg: '#fefce8', color: '#ca8a04'},
                {label: 'Suspended',count: apps.filter(a => a.status === 'rejected').length, bg: '#fff1f2', color: '#e11d48'},
              ].map(s => (
                <span key={s.label} style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: s.bg, color: s.color}}>
                  {s.label}: {s.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      {selected && (
        <ProviderDetailDrawer
          app={selected}
          categoryLabel={categoryMap[selected.businessType] || ''}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onVerify={handleVerify}
          onSuspend={handleSuspend}
          onReactivate={handleReactivate}
          onFinalApprove={handleFinalApprove}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const ProvidersPageWrapper: FC = () => <ProvidersPage />

export {ProvidersPageWrapper}
